/* eslint-disable react/no-unescaped-entities */
import {
  FlexGroup,
  FlexItem,
  HeaderSectionItemButton,
  Icon,
  PageHeader,
  PageHeaderSection,
  Flyout,
  FlyoutBody,
  FlyoutHeader,
  Title,
} from '@antoree/ant-ui';
import logoAntoree from 'assets/images/logo-antoree-v2.svg';
import { NotificationToast } from 'components/Notification';
import { useFirebase, useRedirect } from 'hooks';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl, FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router';
import moment from 'moment';
import LanguageSwitcher from '../LanguageSwitcher';
import MenuItems from './MenuItems';
import NotificationPopoverContainer from './NotificationPopoverContainer';
import UserPopover from './UserPopover';
import { ScreenRecorderManager } from '../SessionModule';
import PendingVideosModal from '../PendingVideosModal/PendingVideosModal';
import SupportTicketFloat from '../SupportTicketFloat';
import DynamicBanner from '../DynamicBanner';
import UnifiedNotification from '../UnifiedNotification';
import {
  videoStorage,
  LOCAL_VIDEOS_STORAGE_CHANGED,
} from '../../utils/videoStorage';
import styles from './MainLayout.module.scss';

const MainLayout: React.FC<{}> = ({ children }) => {
  const [title, setTitle] = useState<string>();
  const currentDay = moment().date();
  const [isPendingVideosOpen, setIsPendingVideosOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hasLocalVideos, setHasLocalVideos] = useState(false);
  useFirebase();
  const location = useLocation();
  const intl = useIntl();

  useEffect(() => {
    let cancelled = false;
    const refreshLocalVideoBadge = async () => {
      try {
        const all = await videoStorage.getAllVideos();
        if (!cancelled) setHasLocalVideos(all.length > 0);
      } catch {
        if (!cancelled) setHasLocalVideos(false);
      }
    };
    refreshLocalVideoBadge();
    window.addEventListener(
      LOCAL_VIDEOS_STORAGE_CHANGED,
      refreshLocalVideoBadge,
    );
    return () => {
      cancelled = true;
      window.removeEventListener(
        LOCAL_VIDEOS_STORAGE_CHANGED,
        refreshLocalVideoBadge,
      );
    };
  }, []);

  useEffect(() => {
    const titleList = location.pathname
      .split('/')
      .filter(item => typeof item === 'string');
    let localTitle = '';
    titleList.forEach((item, index) => {
      // capitalize First Letter
      const capitalizeItem = item.charAt(0).toUpperCase() + item.slice(1);
      localTitle += index === 0 ? `${capitalizeItem}` : `${capitalizeItem} | `;
    });
    setTitle(localTitle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  const { redirectTo } = useRedirect();

  return (
    <>
      <UnifiedNotification />
      <Helmet>
        <title>{`${title} Antoree PWA For Teacher`}</title>
      </Helmet>
      <PageHeader
        paddingSize="l"
        className="sticky top-0 z-10"
        style={{
          backgroundColor: '#fff',
          boxShadow: 'inset 0px -1px 0px #EAEAEA',
          paddingTop: 8,
          paddingBottom: 8,
        }}
        responsive={false}
      >
        <PageHeaderSection>
          <HeaderSectionItemButton onClick={() => redirectTo('/claim-request')}>
            <Icon type={logoAntoree} size="original" />
          </HeaderSectionItemButton>
        </PageHeaderSection>
        <PageHeaderSection>
          <div className={styles.desktopMenu}>
            <MenuItems />
          </div>
          <div className={styles.mobileMenuToggle}>
            <HeaderSectionItemButton onClick={() => setIsMobileMenuOpen(true)}>
              <Icon type="menu" size="m" />
            </HeaderSectionItemButton>
          </div>
        </PageHeaderSection>
        <PageHeaderSection>
          <FlexGroup gutterSize="s" alignItems="center" responsive={false}>
            <FlexItem className={styles.hideOnMobile}>
              <LanguageSwitcher color="primary" />
            </FlexItem>
            <FlexItem>
              <NotificationPopoverContainer />
            </FlexItem>
            <FlexItem className={styles.hideOnMobile}>
              <HeaderSectionItemButton
                onClick={() => setIsPendingVideosOpen(true)}
                title={intl.formatMessage({
                  id: 'pending_videos.tooltip',
                  defaultMessage: 'Saved Videos',
                })}
              >
                <span className={styles.videoIconWrap}>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M23 7l-7 5 7 5V7z" />
                    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                  </svg>
                  {hasLocalVideos ? (
                    <span className={styles.localVideoBadge} aria-hidden />
                  ) : null}
                </span>
              </HeaderSectionItemButton>
            </FlexItem>
            <FlexItem>
              <UserPopover />
            </FlexItem>
          </FlexGroup>
        </PageHeaderSection>
      </PageHeader>

      {isMobileMenuOpen && (
        <Flyout
          onClose={() => setIsMobileMenuOpen(false)}
          size="s"
          aria-labelledby="mobileMenuTitle"
        >
          <FlyoutHeader hasBorder>
            <Title size="s">
              <h2 id="mobileMenuTitle">
                <FormattedMessage defaultMessage="Menu" />
              </h2>
            </Title>
          </FlyoutHeader>
          <FlyoutBody>
            <MenuItems
              variant="mobile"
              // @ts-ignore
              onClick={() => setIsMobileMenuOpen(false)}
            />
          </FlyoutBody>
        </Flyout>
      )}

      <DynamicBanner canShowPopup={false} />
      <div>{children}</div>
      <NotificationToast />
      <ScreenRecorderManager />
      <PendingVideosModal
        isOpen={isPendingVideosOpen}
        onClose={() => setIsPendingVideosOpen(false)}
      />
      <SupportTicketFloat />
    </>
  );
};

export default MainLayout;
